import { BellRing } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { KeyboardEvent, MouseEvent } from 'react';

interface ToolWatchBellProps {
  toolName: string;
  onDismiss: () => void;
  size?: 'xs' | 'sm';
  className?: string;
}

/**
 * Amber bell shown on a contact row / chat card after a watched tool ran.
 * Clicking it dismisses the alert. Rendered as a span with a button role so
 * it can live inside the list row, which is itself a <button>.
 */
export const ToolWatchBell = ({
  toolName,
  onDismiss,
  size = 'sm',
  className,
}: ToolWatchBellProps) => {
  const label = `Watched tool "${toolName}" was invoked — click to dismiss`;

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onDismiss();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.stopPropagation();
      e.preventDefault();
      onDismiss();
    }
  };

  return (
    <span
      role="button"
      tabIndex={0}
      title={label}
      aria-label={label}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'flex shrink-0 cursor-pointer items-center justify-center rounded-md text-amber-500 transition-colors duration-150 hover:bg-amber-400/15 hover:text-amber-600 dark:text-amber-400 dark:hover:text-amber-300',
        size === 'xs' ? 'h-5 w-5' : 'h-7 w-7',
        className,
      )}
    >
      <BellRing className={size === 'xs' ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
    </span>
  );
};
